/* The graders. One function per check type, all with the same contract:

     run(config, ctx) -> { score: 0..1 | null, evidence: string }

   ctx = { output, expected, tools[], steps, ms, cost, seed }

   `evidence` is not decoration. A score with no evidence is a number people
   learn to ignore; a score with the quote that decided it is something they act
   on. teams/contracts.js:122 already made this call for handoff verification —
   same shape here, so the two render through one component.

   Everything except `judge` is real, deterministic logic over the real output
   and the real trajectory. Only the judge is stubbed, and it is stubbed in the
   shape a real judge returns (per-criterion verdicts), so swapping it for a
   model call later changes no caller. */

import { CHECK_TYPES, hashStr } from "@/shared/lib/evals-model";

import type {
  EvalCheck, EvalCheckOutcome, EvalCheckResult, EvalCheckType, EvalContext,
} from "@/shared/types";

const norm = (s: unknown): string => String(s ?? "").trim();
const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/* Enough context around the hit that the user can see WHY it matched.
   Every caller reaches here past a `norm(...)` guard that proves the needle is
   a string, which no narrowing can follow — hence the assertions at the call
   sites here and at the JSON.parse and RegExp below. */
function quote(text: string | undefined, needle: string): string | null {
  const t = String(text || "");
  const i = t.toLowerCase().indexOf(String(needle).toLowerCase());
  if (i < 0) return null;
  const from = Math.max(0, i - 30);
  const to = Math.min(t.length, i + needle.length + 30);
  return `${from > 0 ? "…" : ""}${t.slice(from, to).replace(/\s+/g, " ")}${to < t.length ? "…" : ""}`;
}

const parseList = (v: string | undefined): string[] =>
  String(v || "").split(",").map((s) => s.trim()).filter(Boolean);

/** Every grader has the same contract: config in, score plus evidence out. */
type Runner = (check: EvalCheck, ctx: EvalContext) => EvalCheckOutcome;

const RUNNERS: Record<EvalCheckType, Runner> = {
  contains: (c, ctx) => {
    if (!norm(c.text)) return { score: null, evidence: "No text configured" };
    const hit = quote(ctx.output, c.text as string);
    return hit
      ? { score: 1, evidence: hit }
      : { score: 0, evidence: `“${c.text}” never appears in the output` };
  },

  not_contains: (c, ctx) => {
    if (!norm(c.text)) return { score: null, evidence: "No text configured" };
    const hit = quote(ctx.output, c.text as string);
    return hit
      ? { score: 0, evidence: `Forbidden text present — ${hit}` }
      : { score: 1, evidence: `“${c.text}” absent, as required` };
  },

  regex: (c, ctx) => {
    if (!norm(c.pattern)) return { score: null, evidence: "No pattern configured" };
    let re;
    try {
      re = new RegExp(c.pattern as string, "i");
    } catch {
      // A broken pattern is a broken check, not a failing agent. Skipping keeps
      // it out of the average instead of silently tanking every score.
      return { score: null, evidence: `Invalid pattern: /${c.pattern}/` };
    }
    const m = re.exec(String(ctx.output || ""));
    return m
      ? { score: 1, evidence: `Matched “${m[0].slice(0, 80)}”` }
      : { score: 0, evidence: `No match for /${c.pattern}/` };
  },

  json_valid: (c, ctx) => {
    let parsed;
    const raw = norm(ctx.output);
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      return { score: 0, evidence: `Not JSON — ${(err as Error).message}` };
    }
    const keys = parseList(c.keys);
    if (!keys.length) return { score: 1, evidence: "Parsed as valid JSON" };
    const present = keys.filter((k) => parsed != null && Object.prototype.hasOwnProperty.call(parsed, k));
    const missing = keys.filter((k) => !present.includes(k));
    return {
      // Partial credit: 3 of 4 required keys is genuinely better than 0 of 4,
      // and a 0/1 verdict here hides which direction a change moved.
      score: clamp01(present.length / keys.length),
      evidence: missing.length ? `Missing ${missing.join(", ")}` : `All ${keys.length} required keys present`,
    };
  },

  exact: (c, ctx) => {
    if (!norm(ctx.expected)) return { score: null, evidence: "Case has no expected output" };
    const ok = norm(ctx.output) === norm(ctx.expected);
    return ok
      ? { score: 1, evidence: "Output identical to expected" }
      : { score: 0, evidence: `Got “${norm(ctx.output).slice(0, 60)}…”` };
  },

  /* Two dials, deliberately orthogonal — the tool, and its arguments. Galileo
     scores both together ("the correct tool and for each tool the correct
     arguments"); agentevals keeps argument matching as its own mode because
     "right tool, wrong arguments" is a different bug from "wrong tool", and a
     name-only check waves it straight through. */
  used_tool: (c, ctx) => {
    if (!norm(c.tool)) return { score: null, evidence: "No tool configured" };
    const calls = (ctx.toolCalls || []).filter((t) => t.name === c.tool);
    const n = calls.length || ctx.tools.filter((t) => t === c.tool).length;
    if (!n) {
      return { score: 0, evidence: `Never called ${c.tool} — used ${ctx.tools.length ? ctx.tools.join(", ") : "no tools"}` };
    }
    if (!norm(c.args) || c.argMatch === "ignore") {
      return { score: 1, evidence: `Called ${c.tool} ${n}×` };
    }
    let want;
    try {
      want = JSON.parse(c.args as string);
    } catch {
      // A malformed expectation is a broken check, not a failing agent.
      return { score: null, evidence: `Arguments aren't valid JSON: ${c.args}` };
    }
    const keys = Object.keys(want || {});
    if (!keys.length) return { score: 1, evidence: `Called ${c.tool} ${n}×` };

    // Best matching call — the agent may have called the tool more than once and
    // only got it right on one of them, which still counts as getting it right.
    const scored = calls.map((call) => {
      const got = call.args || {};
      const hits = keys.filter((k) => JSON.stringify(got[k]) === JSON.stringify(want[k]));
      const extra = c.argMatch === "exact" ? Object.keys(got).filter((k) => !keys.includes(k)) : [];
      return { call, hits, extra, got };
    }).sort((a, b) => b.hits.length - a.hits.length || a.extra.length - b.extra.length)[0];

    const missed = keys.filter((k) => !scored.hits.includes(k));
    if (!missed.length && !scored.extra.length) {
      return { score: 1, evidence: `${c.tool}(${keys.map((k) => `${k}=${JSON.stringify(want[k])}`).join(", ")})` };
    }
    if (scored.extra.length && !missed.length) {
      return { score: 0, evidence: `${c.tool} called with unexpected ${scored.extra.join(", ")} — exact match required` };
    }
    const k = missed[0];
    return {
      // Proportional, so "three of four arguments right" is visibly better than
      // "called the wrong tool entirely".
      score: +(scored.hits.length / keys.length).toFixed(2),
      evidence: `${c.tool} — ${k} was ${JSON.stringify(scored.got[k])}, expected ${JSON.stringify(want[k])}${missed.length > 1 ? ` (+${missed.length - 1} more)` : ""}`,
    };
  },

  never_tool: (c, ctx) => {
    if (!norm(c.tool)) return { score: null, evidence: "No tool configured" };
    const n = ctx.tools.filter((t) => t === c.tool).length;
    return n
      ? { score: 0, evidence: `Called ${c.tool} ${n}× — forbidden` }
      : { score: 1, evidence: `${c.tool} never called, as required` };
  },

  tool_order: (c, ctx) => {
    const want = parseList(c.tools);
    if (!want.length) return { score: null, evidence: "No tools configured" };
    if (c.mode === "strict") {
      const ok = want.length === ctx.tools.length && want.every((t, i) => ctx.tools[i] === t);
      return ok
        ? { score: 1, evidence: `Exact sequence: ${ctx.tools.join(" → ")}` }
        : { score: 0, evidence: `Got ${ctx.tools.join(" → ") || "no tools"}` };
    }
    // subset: `want` must appear in order, extra calls between them are fine.
    let i = 0;
    for (const t of ctx.tools) if (t === want[i]) i++;
    return i === want.length
      ? { score: 1, evidence: `${want.join(" → ")} in order within ${ctx.tools.join(" → ")}` }
      : { score: 0, evidence: `Stopped at ${want[i]} — got ${ctx.tools.join(" → ") || "no tools"}` };
  },

  steps_under: (c, ctx) => {
    const n = Number(c.n);
    if (!n) return { score: null, evidence: "No budget configured" };
    if (ctx.steps <= n) return { score: 1, evidence: `${ctx.steps} steps, budget ${n}` };
    // Ratio form, so "took 9 instead of 8" and "took 40 instead of 8" are
    // visibly different failures rather than both reading as a flat 0.
    return { score: clamp01(n / ctx.steps), evidence: `${ctx.steps} steps — ${ctx.steps - n} over budget` };
  },

  budget: (c, ctx) => {
    const over = [];
    if (c.maxCostUsd && ctx.cost > Number(c.maxCostUsd)) over.push(`$${ctx.cost.toFixed(3)} > $${c.maxCostUsd}`);
    if (c.maxMs && ctx.ms > Number(c.maxMs)) over.push(`${Math.round(ctx.ms / 1000)}s > ${Math.round((c.maxMs as number) / 1000)}s`);
    if (!c.maxCostUsd && !c.maxMs) return { score: null, evidence: "No budget configured" };
    return over.length
      ? { score: 0, evidence: `Over budget — ${over.join(", ")}` }
      : { score: 1, evidence: `$${ctx.cost.toFixed(3)} · ${Math.round(ctx.ms / 1000)}s, inside budget` };
  },

  /* Stubbed judge. Real shape, fake model: the rubric is split one criterion per
     line (teams/contracts.js:83 does the same), each is graded, and the score is
     the fraction that passed. Swapping in a real model call replaces the hash and
     nothing else — the caller, the score range and the evidence string all hold. */
  judge: (c, ctx) => {
    const criteria = String(c.rubric || "").split(/\n|(?<=\.)\s+(?=[A-Z])/).map((s) => s.trim()).filter(Boolean);
    if (!criteria.length) return { score: null, evidence: "No rubric configured" };
    // Criterion pass rate tracks the run's quality rather than floating free of
    // it, so a judge score and a deterministic check never disagree about
    // whether the same output was any good.
    const bar = 38 + clamp01(ctx.quality ?? 0.8) * 58;
    const verdicts = criteria.map((text, i) => ({
      text,
      ok: hashStr(`${ctx.seed}|${i}|${text}|${norm(ctx.output).slice(0, 400)}`) % 100 < bar,
    }));
    const failed = verdicts.filter((v) => !v.ok);
    return {
      score: +(verdicts.filter((v) => v.ok).length / verdicts.length).toFixed(2),
      evidence: failed.length
        ? `Failed: ${failed.map((v) => v.text).join(" · ")}`
        : `All ${verdicts.length} criteria met`,
    };
  },
};

/* Single entry point. An unknown type skips rather than throws — a suite that
   outlives a check type must still produce a run. */
export function runCheck(check: EvalCheck, ctx: EvalContext): EvalCheckResult {
  const meta = CHECK_TYPES[check.type];
  const runner = RUNNERS[check.type];
  const base = {
    id: check.id,
    type: check.type,
    label: meta ? meta.describe(check) : check.type,
    weight: check.weight || 1,
  };
  if (!runner) return { ...base, score: null, evidence: `Unknown check type “${check.type}”` };
  try {
    return { ...base, ...runner(check, ctx) };
  } catch (err) {
    return { ...base, score: null, evidence: `Check errored — ${(err as Error).message}` };
  }
}
